/**
 * Snippet capture — turns a selection on the canvas into a Snippet
 * (docs/design/SNIPPETS-SPEC.md "Capture flow").
 *
 * A snippet is a passage, not a scatter: contiguity comes from the beat span
 * (earliest start to latest end among the selected chords), and every chord
 * inside that span is captured — not just the ones literally clicked.
 */
import { v4 as uuidv4 } from 'uuid';
import type { Chord, MusicalKey, Mode } from '@/types/chord';
import type { Snippet, SnippetChord } from '@/types/snippet';
import { deriveCaption } from './snippet-caption';

/**
 * Compute the [start, end) beat span covering every selected chord.
 * Exported for tests; also useful to callers that want to preview the span
 * before committing a capture.
 */
export function captureSpan(selected: Chord[]): { start: number; end: number } {
  const starts = selected.map((c) => c.startBeat);
  const ends = selected.map((c) => c.startBeat + c.duration);
  return { start: Math.min(...starts), end: Math.max(...ends) };
}

function toSnippetChord(chord: Chord, spanStart: number): SnippetChord {
  return {
    scaleDegree: chord.scaleDegree,
    quality: chord.quality,
    extensions: chord.extensions,
    isChromatic: chord.isChromatic,
    chromaticType: chord.chromaticType,
    duration: chord.duration,
    startBeat: chord.startBeat - spanStart,
    voices: chord.voices,
  };
}

export interface CaptureSnippetOptions {
  key: MusicalKey;
  mode: Mode;
  /** Progression title / analyzed file the passage came from, if known. */
  sourceName?: string;
}

/**
 * Capture a snippet from a selection.
 *
 * @param allChords Every chord currently on the canvas (used to fill in the
 *   passage — chords inside the selected span that weren't themselves
 *   selected are captured too).
 * @param selected The chords the user actually selected (>= 2; the "clip
 *   this" affordance only appears at that point).
 */
export function captureSnippet(
  allChords: Chord[],
  selected: Chord[],
  options: CaptureSnippetOptions
): Snippet {
  const { start, end } = captureSpan(selected);

  const passage = allChords
    .filter((c) => c.startBeat >= start && c.startBeat < end)
    .sort((a, b) => a.startBeat - b.startBeat);

  const chords = passage.map((c) => toSnippetChord(c, start));
  const caption = deriveCaption(chords, options.key);

  return {
    id: uuidv4(),
    name: caption,
    sourceName: options.sourceName,
    chords,
    key: options.key,
    mode: options.mode,
    caption,
    createdAt: new Date().toISOString(),
  };
}
